/**
 * Active tenant (company data source) selection.
 *
 * The platform is already tenant-scoped end-to-end (every cost-api query
 * filters by the x-tenant-id header via tenantContextMiddleware) - this file
 * just lets the UI pick which tenant ID to send, instead of every page
 * hardcoding the same literal. No backend changes are needed for switching:
 * the backend already returns whatever data exists for a given tenant_id.
 *
 * DEMO_TENANT_ID is the existing tenant every page has always used - it holds
 * the platform's demo/mock data. APEXON_TENANT_ID is a second, real tenant
 * for Apexon's own accounts. Selecting it today will show an honestly empty
 * dashboard until real AWS/Azure/GCP accounts are onboarded under it -
 * that onboarding is a separate, already-tracked gap (blocked on IT access
 * tickets), not something this switch can fix by itself.
 */

export const DEMO_TENANT_ID = '550e8400-e29b-41d4-a716-446655440000';
export const APEXON_TENANT_ID = '660e8400-e29b-41d4-a716-446655440001';
/**
 * Meridian Retail Group - the curated storyline tenant.
 *
 * Unlike DEMO (procedurally generated smooth numbers) this one is a
 * hand-authored dataset where every recommendation type, anomaly severity and
 * tagging gap exists for a stated business reason, so a full product walkthrough
 * can be given deliberately rather than hoping the random data happens to show
 * something interesting. Backend counterpart: MERIDIAN_TENANT_ID in
 * cost-api/src/services/tenant-data-mode.service.ts - the two must stay in sync.
 */
export const MERIDIAN_TENANT_ID = '770e8400-e29b-41d4-a716-446655440002';

export interface TenantOption {
  id: string;
  label: string;
  description: string;
}

export const TENANT_OPTIONS: TenantOption[] = [
  {
    id: MERIDIAN_TENANT_ID,
    label: 'Meridian Retail Group',
    description: 'Mid-size omnichannel retailer, AWS + Azure + GCP',
  },
  {
    id: DEMO_TENANT_ID,
    label: 'Demo Company (Mock Data)',
    description: 'Sample data for demos and testing',
  },
  {
    id: APEXON_TENANT_ID,
    label: 'Apexon (Real Data)',
    description: 'Apexon’s own configured cloud accounts',
  },
];

const STORAGE_KEY = 'activeTenantId';

export function getActiveTenantId(): string {
  return localStorage.getItem(STORAGE_KEY) || DEMO_TENANT_ID;
}

export function setActiveTenantId(tenantId: string): void {
  localStorage.setItem(STORAGE_KEY, tenantId);
}

/**
 * The tenants the signed-in user is entitled to.
 *
 * TENANT_OPTIONS above is a static list of everything this deployment knows
 * about; this is the subset the server will actually serve. Returns an empty
 * array on any failure so callers keep their existing list rather than
 * rendering an empty switcher - the server enforces regardless, so this
 * listing is presentation, not the security boundary.
 */
export async function getMyTenants(): Promise<TenantOption[]> {
  try {
    const base = import.meta.env.VITE_COST_API_URL
      ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
      : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api/v1');
    const { authHeaders } = await import('./apiHeaders');
    const res = await fetch(`${base}/config/my-tenants`, { headers: authHeaders() });
    if (!res.ok) return [];
    const body = await res.json();
    const ids: string[] = (body?.tenants || []).map((t: any) => t.id);
    // Map back onto TENANT_OPTIONS so the descriptions stay in one place
    // rather than being duplicated in the API response.
    return TENANT_OPTIONS.filter((t) => ids.includes(t.id));
  } catch {
    return [];
  }
}
